import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useDataProvider,
  useGetIdentity,
  useNotify,
  useRedirect,
  useTranslate,
} from "ra-core";
import { Link } from "react-router";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useIsMobile } from "@/hooks/use-mobile";

import { DealProfileSheet } from "../dealProfile/DealProfileSheet";
import { StatusPill } from "../design/StatusPill";
import { formatInstant } from "../guardias/guardiasShared";
import type { CrmDataProvider } from "../providers/types";
import type { WhatsAppInboxRow } from "../types";
import { InboxLinkDialog } from "./InboxLinkDialog";
import { UNREAD_CAP } from "./whatsappInboxRules";
import { WhatsAppThread } from "./WhatsAppThread";

/** The one-line preview of the newest message; media is a reference, never downloaded. */
const previewOf = (
  row: WhatsAppInboxRow,
  translate: (key: string) => string,
): string => {
  const last = row.lastMessage;
  if (!last) return translate("crm.whatsapp.inbox.no_messages");
  return last.body?.trim() || translate("crm.whatsapp.inbox.media_message");
};

const UnmatchedActions = ({ row }: { row: WhatsAppInboxRow }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const redirect = useRedirect();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const { identity } = useGetIdentity();
  const queryClient = useQueryClient();
  const [linkOpen, setLinkOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  // Mobile has no lead form route (`/deals/create`): creating a contact there would leave it orphaned.
  const isMobile = useIsMobile();

  /**
   * 'Crear lead': a contact carrying the conversation's number, then the REGULAR lead form with
   * that contact preselected. `DealCreate` links the conversation once the lead is saved.
   */
  const onCreateLead = async () => {
    setCreating(true);
    try {
      const contactId = await dataProvider.createWhatsAppConversationContact(
        row.conversationId,
      );
      await queryClient.invalidateQueries({ queryKey: ["contacts"] });
      redirect("/deals/create", undefined, undefined, undefined, {
        record: { contact_ids: [contactId], sales_id: identity?.id, index: 0 },
        whatsappConversationId: row.conversationId,
      });
    } catch {
      notify("crm.whatsapp.inbox.create_error", { type: "error" });
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="flex flex-wrap gap-2">
      <Button
        type="button"
        variant="outline"
        className="h-11"
        onClick={() => setLinkOpen(true)}
      >
        {translate("crm.whatsapp.inbox.link")}
      </Button>
      {isMobile ? (
        <p className="w-full text-[15px] text-muted-foreground">
          {translate("crm.whatsapp.inbox.create_lead_desktop_only")}
        </p>
      ) : (
        <Button
          type="button"
          className="h-11"
          disabled={creating}
          onClick={onCreateLead}
        >
          {translate("crm.whatsapp.inbox.create_lead")}
        </Button>
      )}
      <InboxLinkDialog
        conversationId={row.conversationId}
        open={linkOpen}
        onOpenChange={setLinkOpen}
      />
    </div>
  );
};

/**
 * 'Abrir lead': the lead file on desktop; on mobile (no `deals` resource) the lead profile sheet,
 * the same pattern as the Hoy cards.
 */
const OpenLeadAction = ({ row }: { row: WhatsAppInboxRow }) => {
  const translate = useTranslate();
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  if (row.dealId == null) return null;
  if (!isMobile) {
    return (
      <Button asChild variant="outline" className="h-11">
        <Link to={`/deals/${row.dealId}/show`}>
          {translate("crm.whatsapp.inbox.open_lead")}
        </Link>
      </Button>
    );
  }
  return (
    <>
      <Button
        type="button"
        variant="outline"
        className="h-11"
        onClick={() => setOpen(true)}
      >
        {translate("crm.whatsapp.inbox.open_lead")}
      </Button>
      <DealProfileSheet
        dealId={row.dealId}
        leadName={row.leadName ?? translate("crm.whatsapp.inbox.lead_fallback")}
        open={open}
        onOpenChange={setOpen}
      />
    </>
  );
};

/**
 * One inbox conversation: who (the lead, or 'Sin lead'), the 'Responde' badge, the unread count,
 * the window state, the last message and its time. A matched row opens the lead file, where the
 * conversation timeline and composer live; an unmatched one offers 'Vincular a un lead' and
 * 'Crear lead'. No phone number, no number id and no call control anywhere.
 */
export const InboxRow = ({ row }: { row: WhatsAppInboxRow }) => {
  const translate = useTranslate();
  const unread =
    row.unreadCount > UNREAD_CAP ? `${UNREAD_CAP}+` : row.unreadCount;
  const lastAt = row.lastMessage?.at ?? row.lastMessageAt;
  return (
    <Card data-testid="whatsapp-inbox-row">
      <CardContent className="space-y-2 p-4">
        <div className="flex flex-wrap items-center gap-2">
          <p className="min-w-0 flex-1 truncate text-base font-bold">
            {row.dealId != null
              ? (row.leadName ?? translate("crm.whatsapp.inbox.lead_fallback"))
              : translate("crm.whatsapp.inbox.unmatched_name")}
          </p>
          {row.awaitingReply ? (
            <StatusPill
              tone="ok"
              text={translate("crm.whatsapp.badges.responde")}
            />
          ) : null}
          {row.unreadCount > 0 ? (
            <span className="text-sm font-medium tabular-nums">
              {translate("crm.whatsapp.badges.unread", { count: unread })}
            </span>
          ) : null}
        </div>
        <p className="truncate text-base text-muted-foreground">
          {previewOf(row, translate)}
        </p>
        <p className="text-[15px] text-muted-foreground">
          {translate(`crm.whatsapp.inbox.window.${row.windowState}`)}
          {lastAt ? ` · ${formatInstant(lastAt)}` : ""}
        </p>
        <WhatsAppThread conversationId={row.conversationId} />
        {row.dealId != null ? (
          <OpenLeadAction row={row} />
        ) : (
          <UnmatchedActions row={row} />
        )}
      </CardContent>
    </Card>
  );
};
